import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import DownloadIcon from "@atlaskit/icon/core/download";
import ShowMoreVerticalIcon from "@atlaskit/icon/core/show-more-vertical";
import {token} from "@atlaskit/tokens";
import {useQueryClient} from "@tanstack/react-query";
import {createFileRoute, Navigate, useSearch} from "@tanstack/react-router";
import {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {OttopilotBtlActivityStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotBtlActivitiesWorkspaceCapability/domain/OttopilotBtlActivity";
import type {SharedCalendarViewConfig} from "@vesta/vos-types/modules/workspaceLevel/domain/SharedCalendarViewConfig";
import {z} from "zod";
import {BottomDrawer} from "~/components/BottomDrawer";
import {CopyDataDropdownItem} from "~/components/dataActions/CopyDataDropdownItem";
import {ExportDataDropdownItem} from "~/components/dataActions/ExportDataDropdownItem";
import {ExportOptionsDrawerContent} from "~/components/dataActions/ExportOptionsDrawerContent";
import {OttopilotColumnType, type OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import {type FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {OttopilotDataViewer} from "~/components/ottopilotDataViewer/ottopilotDataViewer";
import {useOttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {useOttopilotExportActions} from "~/components/ottopilotDataViewer/useOttopilotExportActions";
import {RowEngagementPanelType, RowEngagementType} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import {type KanbanBoardColumnConfig, ViewType} from "~/components/ottopilotDataViewer/view";
import {SortDirection} from "~/components/ottopilotDataViewer/sortConfiguration";
import {TableOnlyPage} from "~/components/tableOnlyPage";
import {UserKanbanBoardCard} from "~/components/users/userKanbanBoardCard";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useOrganizationalUnitTemplates, usePlatformUserDetails, useWorkspace, useWorkspaceUserTemplates} from "~/contexts/workspaceContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import {type User, UserStatus} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import {userStatusToDisplayName} from "~/utilities/userStatusToDisplayName";
import {parseUserTemplateColumnMap} from "~/domainModels/workspaceModules/usersModuleDhiraagu";
import type {UserDetails} from "@vesta/vos-types/modules/platformLevel/userDetails/domain/UserDetails";
import {isBobcardWorkspace} from "~/domainModels/workspaceSpecific/bobcard";
import type {DhiraaguUserTemplateColumnIds} from "~/domainModels/workspaceSpecific/dhiraagu";
import {isDhiraaguWorkspace} from "~/domainModels/workspaceSpecific/dhiraagu";
import {
    EmotoradRoles,
    EMOTORAD_MY_SALES_TEAM_NAV_VISIBLE_ROLE_TEMPLATE_IDS,
    EMOTORAD__OU_DEALERSHIP_FRANCHISE__COLUMN_ID,
    EMOTORAD__USER__ACTIVE_LIFECYCLE_COUNT__COLUMN_ID,
    EMOTORAD__USER__ASSIGNED_DEALER__COLUMN_ID,
    EMOTORAD__USER__CALLS_COUNT__COLUMN_ID,
    EMOTORAD__USER__CONVERTED_LIFECYCLE_COUNT__COLUMN_ID,
    EMOTORAD__USER__CREATED_BY__COLUMN_ID,
    EMOTORAD__USER__CREATED_ON__COLUMN_ID,
    EMOTORAD__USER__LAST_ACTIVE_ON__COLUMN_ID,
    EMOTORAD__USER__SALES_COUNT__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__AADHAR__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__ADDRESS_CITY__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__ADDRESS_STATE__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__AREA_LOCALITY__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__HOUSE_NUMBER__COLUMN_ID,
    EMOTORAD__USER__STORE_STAFF__PAN__COLUMN_ID,
    EMOTORAD__USER__TEST_RIDE_COUNT__COLUMN_ID,
    EMOTORAD__USER__TOTAL_LEAD_COUNT__COLUMN_ID,
    EMOTORAD__USER__WALKIN_COUNT__COLUMN_ID,
    isEmotoradWorkspace,
} from "~/domainModels/workspaceSpecific/emotorad";
import {
    filterKanbanColumnsForModuleStatusId,
    getEmotoradModuleStatusNavValidStatusIds,
    getEmotoradMySalesTeamStatusNavConfig,
} from "~/domainModels/moduleStatusNav";
import {useSyncModuleStatusFilterFromSearch} from "~/hooks/useSyncModuleStatusFilterFromSearch";
import {useWorkspaceUsersConfiguration} from "~/hooks/workspaceConfiguration";

import {useCheckMobile} from "~/hooks/useCheckMobile";
import {collectUserIdsAccessibleToCurrentUser, filterConfigurationWithUserAccessScope} from "~/utilities/accessControl/userUsersAccess";
import {userHasAccessToOrganisationalUnit} from "~/utilities/accessControl/userOuAccess";
import {
    getBobcardUserLineItemsForRole,
    getBobcardUserVisibleColumnIdsForRole,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_defaultScaffold/users/-components/-line-items-bobcard";
import {
    getDhiraaguUserLineItemsForRole,
    getDhiraaguUserVisibleColumnIdsForRole,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_defaultScaffold/users/-components/-line-items-dhiraagu";
import {
    UserUpdateWrapper,
    UserViewWithFooterComponent,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_defaultScaffold/users/-components/-viewComponent";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {AnankeDropdownMenu} from "@vesta/ananke/components/anankeDropdownMenu";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import type {Integer, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {getUesrs_ColumnValues} from "~/utilities/apis/api";
import {
    convertOttopilotViewerFiltersToPlutoComplexFilter,
    convertOttopilotViewerSortToPlutoOrderBy,
    getWorkspaceUsers,
    getWorkspaceUsersCount,
} from "~/utilities/queries";
import {resolveCalendarEventTypeColor, resolveCalendarEventTypeLabel, resolveCalendarLegendItems} from "~/utilities/sharedCalendarConfig";
import {formatDateTime, transformRowForExport} from "~/utilities/utilities";
import {uniqueUuid} from "@vesta/unique/unique";
import {
    USER_IDENTITY_EMAIL_VIRTUAL_COLUMN_ID,
    USER_IDENTITY_NAME_VIRTUAL_COLUMN_ID,
    USER_IDENTITY_PHONE_NUMBER_VIRTUAL_COLUMN_ID,
} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserIdentityVirtualColumnIds";

const MY_SALES_TEAM_TABLE_VIEW_ID = uniqueUuid`my-sales-team-table-view-id`;
const MY_SALES_TEAM_KANBAN_VIEW_ID = uniqueUuid`my-sales-team-kanban-view-id`;
const MY_SALES_TEAM_CALENDAR_VIEW_ID = uniqueUuid`my-sales-team-calendar-view-id`;

const mySalesTeamKanbanColumnConfig: Array<KanbanBoardColumnConfig> = [
    {
        id: uniqueUuid`my-sales-team-kanban-status-active`,
        displayName: userStatusToDisplayName(UserStatus.active),
        background: token("color.background.accent.green.subtlest"),
        textColor: token("color.text.accent.green"),
        possibleValues: [UserStatus.active],
    },
    {
        id: uniqueUuid`my-sales-team-kanban-status-suspended`,
        displayName: userStatusToDisplayName(UserStatus.suspended),
        background: token("color.background.accent.yellow.subtlest"),
        textColor: token("color.text.accent.yellow"),
        possibleValues: [UserStatus.suspended],
    },
    {
        id: uniqueUuid`my-sales-team-kanban-status-deleted`,
        displayName: userStatusToDisplayName(UserStatus.deleted),
        background: token("color.background.accent.red.subtlest"),
        textColor: token("color.text.accent.red"),
        possibleValues: [UserStatus.deleted],
    },
];

const mySalesTeamSearchSchema = z.object({
    from: z.string().optional(),
    view: z.enum(["table", "kanban", "calendar"]).optional(),
    statusId: z.string().optional(),
});

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/emotorad/my-sales-team/",
)({
    validateSearch: (search) => mySalesTeamSearchSchema.parse(search),
    loader: async () => {},
    component: EmotoradMySalesTeamRouteComponent,
});

function EmotoradMySalesTeamRouteComponent() {
    const {workspace} = useWorkspace();
    const {workspaceUser} = useWorkspaceUser();
    const canAccessEmotoradMySalesTeam =
        isEmotoradWorkspace(workspace) !== true || EMOTORAD_MY_SALES_TEAM_NAV_VISIBLE_ROLE_TEMPLATE_IDS.has(workspaceUser.templateId);

    if (canAccessEmotoradMySalesTeam !== true) {
        return (
            <Navigate
                to="/w/$workspaceId"
                params={{workspaceId: workspace.id}}
            />
        );
    }

    return <RouteComponent />;
}

const MY_SALES_TEAM_DATA_VIEWER_QUERY_KEY_PREFIX = [uniqueUuid`my-sales-team-data-viewer-query-key-prefix`];

/** Event status legend and colors for the My Sales Team calendar (matches Beat planning). */
const EMOTORAD_MY_SALES_TEAM_EVENT_STATUS_CALENDAR_CONFIG: SharedCalendarViewConfig = {
    eventTypeLabelMap: {
        [OttopilotBtlActivityStatus.draft]: "Draft",
        [OttopilotBtlActivityStatus.active]: "Live",
        [OttopilotBtlActivityStatus.scheduled]: "Scheduled",
        [OttopilotBtlActivityStatus.completed]: "Completed",
        [OttopilotBtlActivityStatus.cancelled]: "Cancelled",
        deleted: "Deleted",
    },
    legendItems: [
        {type: OttopilotBtlActivityStatus.draft, label: "Draft", description: "Event is in draft status"},
        {type: OttopilotBtlActivityStatus.active, label: "Live", description: "Event is currently active"},
        {type: OttopilotBtlActivityStatus.scheduled, label: "Scheduled", description: "Event is scheduled for the future"},
        {type: OttopilotBtlActivityStatus.completed, label: "Completed", description: "Event has been completed"},
        {type: OttopilotBtlActivityStatus.cancelled, label: "Cancelled", description: "Event has been cancelled"},
        {type: "deleted", label: "Deleted", description: "Event has been deleted"},
    ],
    eventTypeColorMap: {
        [OttopilotBtlActivityStatus.draft]: {
            backgroundColorToken: "color.background.accent.gray.subtler",
            borderColorToken: "color.border.accent.gray",
            textColorToken: "color.text.accent.gray",
        },
        [OttopilotBtlActivityStatus.active]: {
            backgroundColorToken: "color.background.accent.blue.subtlest",
            borderColorToken: "color.border.accent.blue",
            textColorToken: "color.text.accent.blue",
        },
        [OttopilotBtlActivityStatus.scheduled]: {
            backgroundColorToken: "color.background.accent.yellow.subtlest",
            borderColorToken: "color.border.accent.yellow",
            textColorToken: "color.text.accent.yellow",
        },
        [OttopilotBtlActivityStatus.completed]: {
            backgroundColorToken: "color.background.accent.green.subtlest",
            borderColorToken: "color.border.accent.green",
            textColorToken: "color.text.accent.green",
        },
        [OttopilotBtlActivityStatus.cancelled]: {
            backgroundColorToken: "color.background.accent.purple.subtlest",
            borderColorToken: "color.border.accent.purple",
            textColorToken: "color.text.accent.purple",
        },
        deleted: {
            backgroundColorToken: "color.background.accent.red.subtlest",
            borderColorToken: "color.border.accent.red",
            textColorToken: "color.text.accent.red",
        },
    },
};

type MySalesTeamColumnSpec = {
    id: string;
    displayName: string;
    isDateTime?: boolean;
    platformUserField?: PlatformUserField;
};

type PlatformUserField = keyof Pick<UserDetails, "email" | "name" | "phoneNumber">;

/** Keeps My Sales Team scoped to a single user template even if UI filter state drops or overrides `templateId`. */
function filterConfigurationWithLockedUserTemplate(filterConfiguration: FilterConfiguration, userTemplateId: Uuid): FilterConfiguration {
    const lineItemsWithoutTemplate = filterConfiguration.lineItems.filter((item) => item.columnId !== "templateId");
    return {
        revisionId: filterConfiguration.revisionId,
        lineItems: [
            {
                columnId: "templateId",
                operation: FilterOperation.equals,
                value: userTemplateId,
            },
            ...lineItemsWithoutTemplate,
        ],
    };
}

function getAdditionalColumnValueAsString(user: User, columnId: Uuid): string {
    const value = user.additionalColumnValues[columnId];
    if (typeof value === "string") {
        return value;
    }
    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    if (Array.isArray(value)) {
        return value
            .map((item) => {
                if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
                    return String(item);
                }
                return "";
            })
            .filter((item) => item !== "")
            .join(", ");
    }
    return "";
}

function getPlatformUserFieldAsString(user: User, platformUserDetailsByUserId: ReadonlyMap<Uuid, UserDetails>, field: PlatformUserField): string {
    const value = platformUserDetailsByUserId.get(user.userId)?.[field];
    if (value === null || value === undefined) {
        return "";
    }
    return value.trim();
}

function getMySalesTeamUserTitle(user: User, platformUserDetailsByUserId: ReadonlyMap<Uuid, UserDetails>): string {
    const platformName = getPlatformUserFieldAsString(user, platformUserDetailsByUserId, "name");
    if (platformName !== "") {
        return platformName;
    }

    const platformEmail = getPlatformUserFieldAsString(user, platformUserDetailsByUserId, "email");
    if (platformEmail !== "") {
        return platformEmail;
    }

    return user.userId;
}

function filterMySalesTeamAssignedDealerColumnValues(params: {
    fetchedValues: Array<string>;
    currentUser: User;
    users: Array<User>;
    userTemplates: Array<UserTemplate>;
    organizationalUnits: Array<OrganizationalUnit>;
    organizationalUnitTemplates: Array<OrganizationalUnitTemplate>;
}): Array<string> {
    const {fetchedValues, currentUser, users, userTemplates, organizationalUnits, organizationalUnitTemplates} = params;

    return fetchedValues.filter((value) => {
        const trimmed = value.trim();
        if (trimmed === "") {
            return false;
        }
        return userHasAccessToOrganisationalUnit({
            targetUser: currentUser,
            users: users,
            userTemplates: userTemplates,
            organizationalUnits: organizationalUnits,
            organizationalUnitTemplates: organizationalUnitTemplates,
            targetOrganisationalUnitId: trimmed as Uuid,
        });
    });
}

function formatStatusLabel(value: string): string {
    if (value === String(UserStatus.active)) {
        return userStatusToDisplayName(UserStatus.active);
    }
    if (value === String(UserStatus.suspended)) {
        return userStatusToDisplayName(UserStatus.suspended);
    }
    if (value === String(UserStatus.deleted)) {
        return userStatusToDisplayName(UserStatus.deleted);
    }
    return value === "" ? "-" : value;
}

/**
 * Resolves the Franchise ID for a sales-team user by looking up their assigned dealership OU
 * and reading the Franchise column on it (constant id first, then display-name fallback).
 */
function getEmotoradUserFranchiseId(
    user: User,
    assignedDealerColumnId: Uuid,
    organizationalUnits: ReadonlyArray<OrganizationalUnit>,
    organizationalUnitTemplates: ReadonlyArray<OrganizationalUnitTemplate>,
): string {
    const raw = user.additionalColumnValues[assignedDealerColumnId];
    const dealerOuIds: Array<string> = [];
    if (typeof raw === "string" && raw.trim() !== "") {
        dealerOuIds.push(raw.trim());
    } else if (Array.isArray(raw)) {
        for (const item of raw) {
            if (typeof item === "string" && item.trim() !== "") {
                dealerOuIds.push(item.trim());
            } else if (item !== null && typeof item === "object") {
                const candidate = (item as Record<string, unknown>).id ?? (item as Record<string, unknown>).value;
                if (typeof candidate === "string" && candidate.trim() !== "") {
                    dealerOuIds.push(candidate.trim());
                }
            }
        }
    }
    if (dealerOuIds.length === 0) {
        return "";
    }

    const franchiseLabelCandidates = ["franchise id", "franchise", "franchise code", "franchise no", "franchise number", "dealer code", "dealership code"];
    const resolved: Array<string> = [];
    for (const dealerOuId of dealerOuIds) {
        const ou = organizationalUnits.find((unit) => unit.id === (dealerOuId as Uuid));
        if (ou === undefined) {
            continue;
        }
        const direct = ou.additionalColumnValues[EMOTORAD__OU_DEALERSHIP_FRANCHISE__COLUMN_ID];
        if (typeof direct === "string" && direct.trim() !== "") {
            resolved.push(direct.trim());
            continue;
        }
        if (typeof direct === "number" || typeof direct === "boolean") {
            resolved.push(String(direct));
            continue;
        }
        const template = organizationalUnitTemplates.find((t) => t.id === ou.templateId);
        if (template === undefined) {
            continue;
        }
        let matched = false;
        for (const label of franchiseLabelCandidates) {
            const column = template.additionalColumns.find((col) => col.displayName.trim().toLowerCase() === label);
            if (column === undefined) {
                continue;
            }
            const value = ou.additionalColumnValues[column.id];
            if (typeof value === "string" && value.trim() !== "") {
                resolved.push(value.trim());
                matched = true;
                break;
            }
            if (typeof value === "number" || typeof value === "boolean") {
                resolved.push(String(value));
                matched = true;
                break;
            }
        }
        if (matched === false) {
            // Fallback for single-dealer users: surface the dealer OU id when no franchise
            // column is populated, matching the original intent that Franchise ID = assigned dealer id.
            resolved.push(dealerOuId);
        }
    }
    return resolved.join(", ");
}

function buildMySalesTeamColumns(
    baseColumns: Array<OttopilotColumn<User>>,
    selectedColumns: DhiraaguUserTemplateColumnIds | undefined,
    organizationalUnits: ReadonlyArray<OrganizationalUnit>,
    organizationalUnitTemplates: ReadonlyArray<OrganizationalUnitTemplate>,
    platformUserDetailsByUserId: ReadonlyMap<Uuid, UserDetails>,
): Array<OttopilotColumn<User>> {
    if (selectedColumns === undefined) {
        return baseColumns;
    }

    const assignedDealerUserColumnId: Uuid = selectedColumns.organizationalUnitColumnId ?? EMOTORAD__USER__ASSIGNED_DEALER__COLUMN_ID;
    const franchiseColumnId = selectedColumns.staffCode;
    const franchiseValueGetter = (user: User): string =>
        getEmotoradUserFranchiseId(user, assignedDealerUserColumnId, organizationalUnits, organizationalUnitTemplates);

    const desiredColumns: Array<MySalesTeamColumnSpec> = [
        {id: USER_IDENTITY_NAME_VIRTUAL_COLUMN_ID, displayName: "SSE Name", platformUserField: "name"},
        {id: USER_IDENTITY_PHONE_NUMBER_VIRTUAL_COLUMN_ID, displayName: "Contact Number", platformUserField: "phoneNumber"},
        {id: USER_IDENTITY_EMAIL_VIRTUAL_COLUMN_ID, displayName: "Email ID", platformUserField: "email"},
        {id: "status", displayName: "Status"},
        {
            id: selectedColumns.organizationalUnitColumnId ?? EMOTORAD__USER__ASSIGNED_DEALER__COLUMN_ID,
            displayName: "Assigned Dealer",
        },
        {id: selectedColumns.staffCode, displayName: "Franchise ID"},
        {id: EMOTORAD__USER__STORE_STAFF__ADDRESS_CITY__COLUMN_ID, displayName: "SSE City"},
        {id: EMOTORAD__USER__STORE_STAFF__ADDRESS_STATE__COLUMN_ID, displayName: "SSE State"},
        {id: EMOTORAD__USER__LAST_ACTIVE_ON__COLUMN_ID, displayName: "Last Active On", isDateTime: true},
        {id: EMOTORAD__USER__ACTIVE_LIFECYCLE_COUNT__COLUMN_ID, displayName: "Active Lifecycle Count"},
        {id: EMOTORAD__USER__TOTAL_LEAD_COUNT__COLUMN_ID, displayName: "Total Lead Count"},
        {id: EMOTORAD__USER__CONVERTED_LIFECYCLE_COUNT__COLUMN_ID, displayName: "Converted Lifecycle Count"},
        {id: EMOTORAD__USER__CALLS_COUNT__COLUMN_ID, displayName: "Calls Count"},
        {id: EMOTORAD__USER__WALKIN_COUNT__COLUMN_ID, displayName: "Walk-in Count"},
        {id: EMOTORAD__USER__TEST_RIDE_COUNT__COLUMN_ID, displayName: "Test Ride Count"},
        {id: EMOTORAD__USER__SALES_COUNT__COLUMN_ID, displayName: "Sales Count"},
        {id: EMOTORAD__USER__CREATED_ON__COLUMN_ID, displayName: "Created On", isDateTime: true},
        {id: EMOTORAD__USER__CREATED_BY__COLUMN_ID, displayName: "Created By"},
        {id: EMOTORAD__USER__STORE_STAFF__HOUSE_NUMBER__COLUMN_ID, displayName: "House no."},
        {id: EMOTORAD__USER__STORE_STAFF__AREA_LOCALITY__COLUMN_ID, displayName: "Area/Locality"},
        {id: EMOTORAD__USER__STORE_STAFF__AADHAR__COLUMN_ID, displayName: "Aadhar card"},
        {id: EMOTORAD__USER__STORE_STAFF__PAN__COLUMN_ID, displayName: "PAN card"},
    ];
    const assignedDealerColumnId = selectedColumns.organizationalUnitColumnId ?? EMOTORAD__USER__ASSIGNED_DEALER__COLUMN_ID;

    const baseColumnById = new Map(baseColumns.map((column) => [column.id, column]));
    const customColumns: Array<OttopilotColumn<User>> = desiredColumns.map((columnSpec) => {
        const isFranchiseColumn = columnSpec.id === franchiseColumnId;
        const platformUserField = columnSpec.platformUserField;
        const platformUserValueGetter =
            platformUserField === undefined ? null : (user: User): string => getPlatformUserFieldAsString(user, platformUserDetailsByUserId, platformUserField);
        const existingColumn = baseColumnById.get(columnSpec.id);
        if (existingColumn !== undefined) {
            const updatedColumn: OttopilotColumn<User> = {
                ...existingColumn,
                displayName: columnSpec.displayName,
            };
            if (platformUserValueGetter !== null) {
                Object.assign(updatedColumn, {
                    valueGetter: platformUserValueGetter,
                    valueFormatter: (value: string) => (value === "" ? "-" : value),
                    sortingCapability: false,
                    filteringCapability_contains: false,
                });
            }
            if (columnSpec.id === "status") {
                Object.assign(updatedColumn, {
                    filteringCapability_contains: false,
                    filteringCapability_in: {
                        valuesQueryFn: async () => [UserStatus.active, UserStatus.suspended, UserStatus.deleted],
                    },
                    valueFormatter: (value: string) => formatStatusLabel(value),
                });
            }
            if (columnSpec.id === assignedDealerColumnId) {
                Object.assign(updatedColumn, {
                    filteringCapability_contains: true,
                });
            }
            if (isFranchiseColumn === true) {
                Object.assign(updatedColumn, {
                    type: OttopilotColumnType.string,
                    valueGetter: franchiseValueGetter,
                    valueFormatter: (value: string) => (value === "" ? "-" : value),
                    sortingCapability: true,
                    filteringCapability_contains: true,
                });
            }
            return updatedColumn;
        }

        return {
            id: columnSpec.id,
            type: OttopilotColumnType.string,
            displayName: columnSpec.displayName,
            valueGetter: (user) => {
                if (columnSpec.id === "status") {
                    return userStatusToDisplayName(user.status);
                }
                if (isFranchiseColumn === true) {
                    return franchiseValueGetter(user);
                }
                if (platformUserValueGetter !== null) {
                    return platformUserValueGetter(user);
                }
                return getAdditionalColumnValueAsString(user, columnSpec.id as Uuid);
            },
            valueFormatter: (value) => {
                if (value === "") {
                    return "-";
                }
                if (columnSpec.isDateTime === true) {
                    return formatDateTime(value);
                }
                return value;
            },
            sortingCapability: platformUserValueGetter === null,
            filteringCapability_contains: platformUserValueGetter === null,
        };
    });

    return customColumns;
}

type MySalesTeamExportActions = {
    onExportPng: () => void;
    onExportCsv?: () => void;
    onExportPdf: () => void;
};

function MySalesTeamExportMenu({onExportPng, onExportCsv, onExportPdf}: MySalesTeamExportActions) {
    const isMobile = useCheckMobile();
    const [exportDrawerOpen, setExportDrawerOpen] = useState(false);

    return (
        <>
            {isMobile ? (
                <button
                    type="button"
                    onClick={() => setExportDrawerOpen(true)}
                    className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px] border"
                    style={{
                        borderColor: token("color.border", "#091E4224"),
                        backgroundColor: token("elevation.surface", "#FFFFFF"),
                        color: token("color.icon", "#44546F"),
                    }}
                >
                    <ShowMoreVerticalIcon label="Export options" />
                </button>
            ) : (
                <AnankeDropdownMenu<HTMLButtonElement>
                    placement="bottom-start"
                    trigger={({triggerRef, ...triggerProps}) => (
                        <button
                            {...triggerProps}
                            ref={triggerRef}
                            type="button"
                            className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px] border"
                            style={{
                                borderColor: token("color.border", "#091E4224"),
                                backgroundColor: token("elevation.surface", "#FFFFFF"),
                                color: token("color.icon", "#44546F"),
                            }}
                        >
                            <ShowMoreVerticalIcon label="Export options" />
                        </button>
                    )}
                >
                    <AnankeDropdownItemGroup>
                        {/* <AnankeDropdownItem
                            onClick={() => onExportPng()}
                            elemBefore={<DownloadIcon label="" color={token("color.icon")} />}
                            elemAfter={
                                <ChevronRightIcon label="" size="small" color={token("color.icon.subtle")} />
                            }
                        >
                            Export as PNG
                        </AnankeDropdownItem> */}
                        {onExportCsv !== undefined ? <AnankeDropdownItem
                            onClick={onExportCsv}
                            elemBefore={
                                <DownloadIcon
                                    label=""
                                    color={token("color.icon")}
                                />
                            }
                            elemAfter={
                                <ChevronRightIcon
                                    label=""
                                    size="small"
                                    color={token("color.icon.subtle")}
                                />
                            }
                        >
                            Export as CSV
                        </AnankeDropdownItem> : null}
                        <AnankeDropdownItem
                            onClick={() => onExportPdf()}
                            elemBefore={
                                <DownloadIcon
                                    label=""
                                    color={token("color.icon")}
                                />
                            }
                            elemAfter={
                                <ChevronRightIcon
                                    label=""
                                    size="small"
                                    color={token("color.icon.subtle")}
                                />
                            }
                        >
                            Export as PDF
                        </AnankeDropdownItem>
                    </AnankeDropdownItemGroup>
                </AnankeDropdownMenu>
            )}
            {isMobile && (
                <BottomDrawer
                    open={exportDrawerOpen}
                    setOpen={setExportDrawerOpen}
                    title="Export"
                    fitContentOnOpen={true}
                >
                    <ExportOptionsDrawerContent
                        onExportAsPng={() => {
                            onExportPng();
                            setExportDrawerOpen(false);
                        }}
                        onExportAsCsv={onExportCsv === undefined ? undefined : () => {
                            onExportCsv();
                            setExportDrawerOpen(false);
                        }}
                        onExportAsPdf={() => {
                            onExportPdf();
                            setExportDrawerOpen(false);
                        }}
                    />
                </BottomDrawer>
            )}
        </>
    );
}

function RouteComponent() {
    const queryClient = useQueryClient();
    const [exportActions, setExportActions] = useState<MySalesTeamExportActions | null>(null);
    const handleRegisterExportActions = useCallback((actions: MySalesTeamExportActions | null) => {
        setExportActions(actions);
    }, []);

    return (
        <TableOnlyPage
            title="My Sales Team"
            titleRightSection={exportActions !== null ? <MySalesTeamExportMenu {...exportActions} /> : null}
            bottomSection={
                <MySalesTeamDataViewerWithController
                    onRegisterExportActions={handleRegisterExportActions}
                    invalidateUsersQuery={() => {
                        queryClient.invalidateQueries({queryKey: ["ottopilotDataViewer", ...MY_SALES_TEAM_DATA_VIEWER_QUERY_KEY_PREFIX]});
                    }}
                />
            }
        />
    );
}

function MySalesTeamDataViewerWithController({
    invalidateUsersQuery,
    onRegisterExportActions,
}: {
    invalidateUsersQuery: () => void;
    onRegisterExportActions?: (actions: MySalesTeamExportActions | null) => void;
}) {
    const search = useSearch({
        from: "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/emotorad/my-sales-team/",
    });
    const usersModuleConfiguration = useWorkspaceUsersConfiguration();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser, workspaceUser} = useWorkspaceUser();
    const {workspace} = useWorkspace();
    const users = useUsers();
    const userTemplates = useWorkspaceUserTemplates();
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const platformUserDetails = usePlatformUserDetails();

    const isDhiraagu = isDhiraaguWorkspace(workspace);
    const isBobcard = isBobcardWorkspace(workspace);
    const isEmotorad = isEmotoradWorkspace(workspace);
    const hasColumnMap = isDhiraagu || isBobcard || isEmotorad;
    const platformUserDetailsByUserId = useMemo(
        () => new Map<Uuid, UserDetails>(platformUserDetails.map((platformUser) => [platformUser.userId, platformUser])),
        [platformUserDetails],
    );
    const platformUserNameByUserId = useMemo(
        () => new Map<Uuid, string | null>(platformUserDetails.map((platformUser) => [platformUser.userId, platformUser.name])),
        [platformUserDetails],
    );

    // Hardcode to Store Sales Executive for Emotorad
    const selectedUserTemplateId: Uuid = EmotoradRoles.StoreSalesExecutive as Uuid;

    let parsedUserTemplateColumnMap: Record<string, DhiraaguUserTemplateColumnIds> | null = null;
    if (hasColumnMap === true) {
        parsedUserTemplateColumnMap = parseUserTemplateColumnMap(usersModuleConfiguration.userTemplateColumnMap);
    }

    const columnMapDeps = {
        users: users,
        userTemplates: userTemplates,
        userTemplateColumnMap: parsedUserTemplateColumnMap!,
        organizationalUnits: organizationalUnits,
        organizationalUnitTemplates: organizationalUnitTemplates,
        platformUserDetailsByUserId: platformUserDetailsByUserId,
    };

    const roleLineItems: Array<OttopilotColumn<User>> =
        isDhiraagu || isEmotorad
            ? getDhiraaguUserLineItemsForRole(columnMapDeps, selectedUserTemplateId)
            : getBobcardUserLineItemsForRole(columnMapDeps, selectedUserTemplateId);

    const roleVisibleColumnsTable =
        isDhiraagu || isEmotorad
            ? getDhiraaguUserVisibleColumnIdsForRole(selectedUserTemplateId, parsedUserTemplateColumnMap!)
            : getBobcardUserVisibleColumnIdsForRole(selectedUserTemplateId, parsedUserTemplateColumnMap!);

    const selectedTemplateColumns = parsedUserTemplateColumnMap?.[selectedUserTemplateId];
    const baseLineItems: Array<OttopilotColumn<User>> = isEmotorad
        ? buildMySalesTeamColumns(roleLineItems, selectedTemplateColumns, organizationalUnits, organizationalUnitTemplates, platformUserDetailsByUserId)
        : roleLineItems;
    const assignedDealerColumnId = selectedTemplateColumns?.organizationalUnitColumnId ?? EMOTORAD__USER__ASSIGNED_DEALER__COLUMN_ID;
    const mySalesTeamKanbanColumnConfigForStatus = useMemo(
        () => filterKanbanColumnsForModuleStatusId(mySalesTeamKanbanColumnConfig, search.statusId),
        [search.statusId],
    );
    const accessibleUserIdsForCurrentUser = useMemo(() => {
        if (isEmotorad !== true) {
            return null;
        }
        return collectUserIdsAccessibleToCurrentUser({
            currentUser: workspaceUser,
            users: users,
            userTemplates: userTemplates,
            candidateUsers: users.filter((user) => user.templateId === selectedUserTemplateId),
        });
    }, [isEmotorad, workspaceUser, users, userTemplates, selectedUserTemplateId]);
    const lineItems: Array<OttopilotColumn<User>> = useMemo(() => {
        if (isEmotorad !== true) {
            return baseLineItems;
        }
        return baseLineItems.map((column) => {
            if (column.id !== assignedDealerColumnId) {
                return column;
            }
            return {
                ...column,
                filteringCapability_contains: true,
                customFilter: (args) => {
                    const selectedItems = args.ephemeralValue.lineItems;
                    if (selectedItems === null) {
                        args.controller.setFilterConfiguration((prev) => ({
                            revisionId: generateUuid(),
                            lineItems: prev.lineItems.filter((lineItem) => lineItem.columnId !== assignedDealerColumnId),
                        }));
                        args.closePopup();
                        return;
                    }
                    const selectedValue = selectedItems.length === 0 ? "__no_assigned_dealer_match__" : (selectedItems[0]?.trim() ?? "");
                    args.controller.setFilterConfiguration((prev) => ({
                        revisionId: generateUuid(),
                        lineItems: [
                            ...prev.lineItems.filter((lineItem) => lineItem.columnId !== assignedDealerColumnId),
                            {
                                columnId: assignedDealerColumnId,
                                operation: FilterOperation.contains,
                                value: selectedValue,
                            },
                        ],
                    }));
                    args.closePopup();
                },
            };
        });
    }, [assignedDealerColumnId, baseLineItems, isEmotorad]);

    const visibleColumnsTable = isEmotorad ? lineItems.map((column) => column.id) : roleVisibleColumnsTable;

    const lineItemIdsSortedKey = [...new Set(lineItems.map((column) => column.id))].sort().join("\0");
    const mySalesTeamEmotoradDefaultVisibleFilterColumnIds = useMemo(() => {
        if (isEmotorad !== true || selectedTemplateColumns === undefined) {
            return [] as Array<string>;
        }
        const visible = new Set(lineItemIdsSortedKey.split("\0").filter((id) => id !== ""));
        const preferred: Array<string> = ["status", String(assignedDealerColumnId)];
        const seen = new Set<string>();
        const out: Array<string> = [];
        for (const id of preferred) {
            if (visible.has(id) === true && seen.has(id) !== true) {
                seen.add(id);
                out.push(id);
            }
        }
        return out;
    }, [assignedDealerColumnId, isEmotorad, lineItemIdsSortedKey, selectedTemplateColumns]);

    const initialVisibleFiltersForController =
        mySalesTeamEmotoradDefaultVisibleFilterColumnIds.length > 0
            ? {
                  revisionId: uniqueUuid`a1b2c3d4-e5f6-4789-a012-3456789abcde`,
                  lineItems: mySalesTeamEmotoradDefaultVisibleFilterColumnIds,
              }
            : undefined;
    const mySalesTeamIdentityAdditionalColumnIds = useMemo<ReadonlySet<Uuid>>(() => new Set<Uuid>(), []);

    const userSearchColumns =
        isEmotorad === true
            ? [USER_IDENTITY_NAME_VIRTUAL_COLUMN_ID, USER_IDENTITY_EMAIL_VIRTUAL_COLUMN_ID, USER_IDENTITY_PHONE_NUMBER_VIRTUAL_COLUMN_ID]
            : parsedUserTemplateColumnMap === null
              ? []
              : (() => {
                    const selectedColumns = parsedUserTemplateColumnMap[selectedUserTemplateId];
                    if (selectedColumns === undefined) {
                        return [];
                    }
                    return [
                        USER_IDENTITY_NAME_VIRTUAL_COLUMN_ID,
                        USER_IDENTITY_EMAIL_VIRTUAL_COLUMN_ID,
                        USER_IDENTITY_PHONE_NUMBER_VIRTUAL_COLUMN_ID,
                        selectedColumns.displayName,
                    ];
                })();

    const templateSearchGroups = usersModuleConfiguration.usersPage?.ottopilotDataViewerConfig?.templateSearchGroups;
    /** Emotorad: grouped search can OR multiple templates; this page must list Store Sales Executives only. */
    const usersApiTemplateSearchGroups = isEmotorad === true ? undefined : templateSearchGroups;

    const {showFlag} = useAnankeFlags();
    const mySalesTeamCalendarLegendItems = useMemo(
        () => resolveCalendarLegendItems(EMOTORAD_MY_SALES_TEAM_EVENT_STATUS_CALENDAR_CONFIG, EMOTORAD_MY_SALES_TEAM_EVENT_STATUS_CALENDAR_CONFIG),
        [],
    );
    const getMySalesTeamCalendarTypeLabel = useCallback((type: string): string => {
        return resolveCalendarEventTypeLabel(
            type,
            EMOTORAD_MY_SALES_TEAM_EVENT_STATUS_CALENDAR_CONFIG,
            EMOTORAD_MY_SALES_TEAM_EVENT_STATUS_CALENDAR_CONFIG,
            "Deleted",
        );
    }, []);
    const getMySalesTeamCalendarTypeColor = useCallback((type: string | undefined) => {
        return resolveCalendarEventTypeColor(type, EMOTORAD_MY_SALES_TEAM_EVENT_STATUS_CALENDAR_CONFIG, EMOTORAD_MY_SALES_TEAM_EVENT_STATUS_CALENDAR_CONFIG);
    }, []);
    const usersDataViewerController = useOttopilotDataViewerController<User>({
        rowDisplayNameVariants: isEmotorad
            ? {
                  singularSentenceCase: "SSE",
                  pluralSentenceCase: "SSEs",
                  singularLowerCase: "SSE",
                  pluralLowerCase: "SSEs",
              }
            : {
                  singularSentenceCase: "Sales Executive",
                  pluralSentenceCase: "Sales Executives",
                  singularLowerCase: "sales executive",
                  pluralLowerCase: "sales executives",
              },
        queryKeyPrefix: MY_SALES_TEAM_DATA_VIEWER_QUERY_KEY_PREFIX,
        calendarDataSource: "direct",
        searchColumns: userSearchColumns,
        searchPlaceholder: isEmotorad ? "Search SSEs..." : "Search sales executives...",
        filterClearConfig: {
            preservedFilterLineItemColumnIds: ["templateId"],
        },
        initialFilterConfiguration: {
            revisionId: uniqueUuid`my-sales-team-initial-filter-revision-id`,
            lineItems: [
                {
                    columnId: "templateId",
                    operation: FilterOperation.equals,
                    value: selectedUserTemplateId,
                },
            ],
        },
        initialVisibleFilters: initialVisibleFiltersForController,
        initialSortConfiguration: {
            revisionId: uniqueUuid`my-sales-team-initial-sort-revision-id`,
            lineItems: [
                {
                    columnId: "updatedAt",
                    direction: SortDirection.descending,
                },
            ],
        },
        rowsQueryFn: async ({filterConfiguration, limit, offset, sortConfiguration, searchConfiguration}) => {
            const templateLockedFilterConfiguration =
                isEmotorad === true ? filterConfigurationWithLockedUserTemplate(filterConfiguration, selectedUserTemplateId) : filterConfiguration;
            const effectiveFilterConfiguration =
                isEmotorad === true
                    ? filterConfigurationWithUserAccessScope(templateLockedFilterConfiguration, accessibleUserIdsForCurrentUser)
                    : templateLockedFilterConfiguration;
            if (effectiveFilterConfiguration === null) {
                return [];
            }
            const usersResult = await getWorkspaceUsers({
                workspaceId: workspace.id,
                offset: offset,
                limit: limit,
                complexFilters: convertOttopilotViewerFiltersToPlutoComplexFilter({
                    filterConfiguration: effectiveFilterConfiguration,
                    searchConfiguration: searchConfiguration,
                    templateSearchGroups: usersApiTemplateSearchGroups,
                }),
                orderBy: convertOttopilotViewerSortToPlutoOrderBy(sortConfiguration),
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (usersResult.success !== true) {
                throw usersResult.error;
            }
            return usersResult.data.map((user) => {
                const displayTitle = getMySalesTeamUserTitle(user, platformUserDetailsByUserId);
                return {
                    ...user,
                    title: displayTitle,
                    startDate: user.createdAt,
                    endDate: user.createdAt,
                    isAlldayEvent: false,
                } as User;
            });
        },
        rowCountQueryFn: async ({filterConfiguration, searchConfiguration}) => {
            const templateLockedFilterConfiguration =
                isEmotorad === true ? filterConfigurationWithLockedUserTemplate(filterConfiguration, selectedUserTemplateId) : filterConfiguration;
            const effectiveFilterConfiguration =
                isEmotorad === true
                    ? filterConfigurationWithUserAccessScope(templateLockedFilterConfiguration, accessibleUserIdsForCurrentUser)
                    : templateLockedFilterConfiguration;
            if (effectiveFilterConfiguration === null) {
                return 0 as Integer;
            }
            const usersLengthResult = await getWorkspaceUsersCount({
                workspaceId: workspace.id,
                complexFilters: convertOttopilotViewerFiltersToPlutoComplexFilter({
                    filterConfiguration: effectiveFilterConfiguration,
                    searchConfiguration: searchConfiguration,
                    templateSearchGroups: usersApiTemplateSearchGroups,
                }),
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (usersLengthResult.success !== true) {
                throw usersLengthResult.error;
            }
            return usersLengthResult.data;
        },
        columnValuesQueryFn: async ({columnId}) => {
            const result = await getUesrs_ColumnValues({
                workspaceId: workspace.id,
                columnId: columnId,
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
                templateId: selectedUserTemplateId,
            });
            if (result.success !== true) {
                return [];
            }
            const fetchedValues = result.data;
            if (isEmotorad !== true) {
                return fetchedValues;
            }

            if (columnId === String(assignedDealerColumnId)) {
                return filterMySalesTeamAssignedDealerColumnValues({
                    fetchedValues: fetchedValues,
                    currentUser: workspaceUser,
                    users: users,
                    userTemplates: userTemplates,
                    organizationalUnits: organizationalUnits,
                    organizationalUnitTemplates: organizationalUnitTemplates,
                });
            }

            return fetchedValues;
        },
        columns: {
            revisionId: uniqueUuid`my-sales-team-data-viewer-revision-id`,
            lineItems: lineItems,
        },
        views: [
            {
                id: MY_SALES_TEAM_TABLE_VIEW_ID,
                viewType: ViewType.table,
                visibleColumns: visibleColumnsTable,
            },
            {
                id: MY_SALES_TEAM_KANBAN_VIEW_ID,
                viewType: ViewType.kanbanBoard,
                filterFieldId: "status",
                columnConfig: mySalesTeamKanbanColumnConfigForStatus,
                cardRenderer: (user: User) => (
                    <UserKanbanBoardCard
                        user={user}
                        allUsers={users}
                        userTemplates={userTemplates}
                        platformUserNameByUserId={platformUserNameByUserId}
                        platformUserDetailsByUserId={platformUserDetailsByUserId}
                        excludedAdditionalColumnIds={mySalesTeamIdentityAdditionalColumnIds}
                        assignedDealerColumnId={selectedTemplateColumns?.organizationalUnitColumnId ?? EMOTORAD__USER__ASSIGNED_DEALER__COLUMN_ID}
                        showAssignedDealerAndSseContactDetails={true}
                        organizationalUnits={organizationalUnits}
                        organizationalUnitTemplates={organizationalUnitTemplates}
                        onViewClick={() => {
                            usersDataViewerController.setCurrentlyOpenRowEngagementPanel({
                                rowEngagementType: RowEngagementType.read,
                                rowEngagementPanelType: RowEngagementPanelType.sidebar,
                                rowEngagementData: user,
                            });
                        }}
                        onEditClick={() => {
                            usersDataViewerController.setCurrentlyOpenRowEngagementPanel({
                                rowEngagementType: RowEngagementType.update,
                                rowEngagementPanelType: RowEngagementPanelType.sidebar,
                                rowEngagementData: user,
                            });
                        }}
                    />
                ),
            },
            {
                id: MY_SALES_TEAM_CALENDAR_VIEW_ID,
                viewType: ViewType.calendar,
                ...(isEmotorad === true
                    ? {
                          eventOpensDaySummaryModal: true,
                          calendarDaySummaryModalPresentation: {
                              kind: "emotoradMySalesTeam",
                              entitySingularNoun: "SSE",
                              entityPluralNoun: "SSEs",
                              getSecondaryLine: (user: User) => {
                                  const platformPhoneNumber = getPlatformUserFieldAsString(user, platformUserDetailsByUserId, "phoneNumber");
                                  return platformPhoneNumber;
                              },
                          },
                          legendItems: mySalesTeamCalendarLegendItems,
                          getEventTypeLabel: getMySalesTeamCalendarTypeLabel,
                          getEventColor: getMySalesTeamCalendarTypeColor,
                      }
                    : {}),
            },
        ],
        listCardChrome: null,
        readPanelComponent: (row, isExpanded) => (
            <UserViewWithFooterComponent
                user={row}
                isExpanded={isExpanded}
                setCurrentlyOpenRowEngagementPanel={usersDataViewerController.setCurrentlyOpenRowEngagementPanel}
                invalidateUsersQuery={invalidateUsersQuery}
                platformUserNameByUserId={platformUserNameByUserId}
                platformUserDetailsByUserId={platformUserDetailsByUserId}
                onClose={() => {
                    usersDataViewerController.setCurrentlyOpenRowEngagementPanel({
                        rowEngagementType: RowEngagementType.none,
                        rowEngagementPanelType: RowEngagementPanelType.none,
                        rowEngagementData: null,
                    });
                }}
            />
        ),
        updatePanelComponent: (row, isExpanded) => {
            return (
                <UserUpdateWrapper
                    user={row}
                    isExpanded={isExpanded}
                    onClose={() => {
                        usersDataViewerController.setCurrentlyOpenRowEngagementPanel({
                            rowEngagementType: RowEngagementType.none,
                            rowEngagementPanelType: RowEngagementPanelType.none,
                            rowEngagementData: null,
                        });
                    }}
                    invalidateUsersQuery={invalidateUsersQuery}
                    platformUserDetailsByUserId={platformUserDetailsByUserId}
                />
            );
        },
        rowEngagementDropdownOptions: (row) => {
            const dataToExport = transformRowForExport(row, lineItems);
            return [
                <ExportDataDropdownItem
                    key="export"


                    isVisible={false}
                />,
                <CopyDataDropdownItem
                    key="copy"
                    data={dataToExport}
                    showFlag={showFlag}
                />,
            ];
        },
        initialViewId: MY_SALES_TEAM_TABLE_VIEW_ID,
    });

    const emotoradMySalesTeamStatusNavConfig = useMemo(() => {
        if (isEmotorad !== true) {
            return null;
        }
        return getEmotoradMySalesTeamStatusNavConfig();
    }, [isEmotorad]);

    useSyncModuleStatusFilterFromSearch({
        statusColumnId: emotoradMySalesTeamStatusNavConfig?.statusColumnId ?? null,
        setFilterConfiguration: usersDataViewerController.setFilterConfiguration,
        enabled: isEmotorad === true && emotoradMySalesTeamStatusNavConfig !== null,
        validStatusIds: getEmotoradModuleStatusNavValidStatusIds(emotoradMySalesTeamStatusNavConfig),
    });

    useEffect(() => {
        const viewId =
            search.view === "kanban" ? MY_SALES_TEAM_KANBAN_VIEW_ID : search.view === "calendar" ? MY_SALES_TEAM_CALENDAR_VIEW_ID : MY_SALES_TEAM_TABLE_VIEW_ID;
        usersDataViewerController.setCurrentViewId(viewId);
    }, [search.view, usersDataViewerController.setCurrentViewId]);

    const dbExportActions = useOttopilotExportActions({
        controller: usersDataViewerController,

        pngFilename: "sales-team-export.png",
        pdfFilename: "sales-team-export.pdf",
        documentTitle: "My Sales Team",
        csvExportMode: {kind: "disabled"},
        onError: (message) => {
            showFlag({
                title: "Export failed",
                description: message,
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 3,
            });
        },
    });

    useLayoutEffect(() => {
        if (onRegisterExportActions === undefined) {
            return;
        }

        onRegisterExportActions({
            onExportPng: dbExportActions.onExportPng,
            onExportPdf: dbExportActions.onExportPdf,
        });

        return () => {
            onRegisterExportActions(null);
        };
    }, [dbExportActions.onExportCsv, dbExportActions.onExportPdf, dbExportActions.onExportPng, onRegisterExportActions]);

    return <OttopilotDataViewer controller={usersDataViewerController} />;
}
